import { Col, Row } from "react-bootstrap";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { useGet, usePost } from "@/apiHooks/HttpFunctions";

import BasicModal from "@/Modals/BasicModal";
import RenderToast from "@/components/RenderToast";
import PlayerTypeInitialValues from "@/data/Profiles/PlayerTypes/initial-values";
import PlayerTypeSchema from "@/schema/Profiles/PlayerTypes/PlayerTypeSchema";
import StatusInput from "@/components/StatusInput";
import { playerTypesApi, sportsMasterDataApi } from "@/apis/apis";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import { LIST_MODE, LIST_PER_PAGE } from "@/constant/Lists/listMode";
import useModalGetOptions from "@/CustomHooks/Modal/useModalGetOptions";
import AsyncDropdown from "@/components/AsyncDropdown/AsyncDropdown";
import BasicInput from "@/components/BasicInput";

const PlayerTypeModal = forwardRef((_, ref) => {
  const [modalState, setModalState] = useState({ isOpen: false, id: null });

  const showApi = modalState.id ? `${playerTypesApi}/${modalState.id}` : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Player Type ${action} successfully.`,
    });
  };
  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePost({
    apiURL: `${playerTypesApi}/${modalState.id}`,
    isFormData: true,
    onSuccess: handleSuccess("edited"),
  });
  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: playerTypesApi,
    isFormData: true,
    onSuccess: handleSuccess("added"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: PlayerTypeInitialValues(modalState.id ? showData : null),
    validationSchema: PlayerTypeSchema(!!modalState.id),
    onSubmit: (values) => handleSubmit(values),
  });

  const baseQueryParams = `?mode=${LIST_MODE.SELECT}&perPage=${LIST_PER_PAGE}&filter[keyword]=`;

  const sportListApi = `${sportsMasterDataApi}${baseQueryParams}`;
  const {
    loading: sportLoading,
    defaultOptions: sportDefaultOptions,
    debouncedFetchOptions: sportFetchOptions,
    getSelectedOption: sportGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: sportListApi,
    formikValue: formik.values.sport_id,
    formikInitialValue: formik.initialValues.sport_id,
    showData: !!modalState?.id && showData?.data?.sport,
  });

  const handleSubmit = (values) => {
    const formData = new FormData();
    if (modalState.id) formData.append("_method", "PUT");
    formData.append("status", values.status);
    formData.append("sport_id", values.sport_id);
    formData.append("name", values.name);
    formData.append("code", values.code);

    modalState.id ? saveEdit(formData) : saveCreate(formData);
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={modalState.id ? "Edit Player Type" : "Add Player Type"}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"code"}
            saveError={saveError}
            label="ID"
          />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"name"}
            saveError={saveError}
            label="Player Type"
          />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <AsyncDropdown
            label="Sport"
            placeholder="No sport selected. Type to search..."
            defaultOptions={sportDefaultOptions}
            loadOptions={sportFetchOptions}
            isLoading={sportLoading}
            value={sportGetSelectedOption()}
            onChange={(selected) => {
              formik.setFieldValue(
                "sport_id",
                selected?.value || formik.values.sport_id || null
              );
            }}
            errorText={
              (formik.touched.sport_id && formik.errors.sport_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.sport_id?.join(" "))
            }
          />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <StatusInput formik={formik} saveError={saveError} />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default PlayerTypeModal;
